import type { GeoLocation } from '@smg-real-estate/geo-libs';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';

import { useDirectoryBreadcrumbs } from '@/composables/useDirectoryBreadcrumbs';
import { isPageIndexable } from '@/helpers/SEO/isPageIndexable';
import type { DirectoryType } from '@/types/directoryType';

/**
 * Shared breadcrumb trail and JSON-LD indexability gate for directory SRP pages.
 * National hub (zero locations) is never indexable for structured data.
 * Uses the same location and pagination rules as {@link isPageIndexable}.
 */
export function useDirectorySrpJsonLdContext(
  directoryType: MaybeRefOrGetter<DirectoryType>,
  searchedLocations: MaybeRefOrGetter<GeoLocation[] | null | undefined>,
  currentPage: MaybeRefOrGetter<number>,
) {
  const resolvedLocations = computed(() => toValue(searchedLocations) ?? []);
  const locationCount = computed(() => resolvedLocations.value.length);
  const breadcrumbs = useDirectoryBreadcrumbs(directoryType, resolvedLocations);
  const isIndexableForJsonLd = computed(
    () =>
      isPageIndexable(toValue(searchedLocations), toValue(currentPage)) &&
      locationCount.value === 1,
  );

  return {
    resolvedLocations,
    locationCount,
    breadcrumbs,
    isIndexableForJsonLd,
  };
}
